import { useState } from 'react';
import { Copy, Plus, X } from 'lucide-react';
import { Button } from '@sara/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import { Input } from '@sara/ui/components/input';
import { Switch } from '@sara/ui/components/switch';
import { Badge } from '@sara/ui/components/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';
import { toast } from '@sara/ui/components/sonner';
import { cn } from '@sara/ui/lib/utils';
import { slugifyVariableKey } from './variable-key';
import { templateVariableSchema, VARIABLE_TYPE_LABELS, VARIABLE_TYPES } from './schemas';
import type { TemplateVariableValue, VariableType } from './schemas';

interface EditVariableDialogProps {
  open: boolean;
  /** The variable being edited, or null when adding a new one. */
  initial: TemplateVariableValue | null;
  /** Keys already used by other variables — for the uniqueness check. */
  takenKeys: string[];
  onSave: (value: TemplateVariableValue) => void;
  onOpenChange: (open: boolean) => void;
}

const EMPTY: TemplateVariableValue = {
  key: '',
  name: '',
  type: 'TEXT',
  required: false,
  description: '',
  allowedValues: [],
  defaultValue: '',
};

const labelClass = 'text-xs font-medium text-foreground/80';
const helpClass = 'text-xs leading-snug text-muted-foreground';

/**
 * Add or edit a single input variable. Local state (not the parent form) so it
 * can be cancelled without touching the version; commits the validated value
 * back through onSave. The {{token}} is derived live from the name — it's the
 * one thing an author pastes into the agent prompt, so it stays front and centre.
 */
export function EditVariableDialog({
  open,
  initial,
  takenKeys,
  onSave,
  onOpenChange,
}: EditVariableDialogProps) {
  const [draft, setDraft] = useState<TemplateVariableValue>(initial ?? EMPTY);
  // Once the author edits the key by hand, stop auto-deriving it from the name.
  const [keyEdited, setKeyEdited] = useState(false);
  const [chipDraft, setChipDraft] = useState('');
  const [errors, setErrors] = useState<Partial<Record<keyof TemplateVariableValue, string>>>({});

  // Re-seed local state whenever the dialog opens for a different variable.
  const seedKey = `${open}:${initial?.key ?? '__new__'}`;
  const [lastSeed, setLastSeed] = useState(seedKey);
  if (seedKey !== lastSeed) {
    setLastSeed(seedKey);
    setDraft(initial ?? EMPTY);
    setKeyEdited(Boolean(initial));
    setChipDraft('');
    setErrors({});
  }

  const token = draft.key || 'variable';
  const isEditing = Boolean(initial);

  function set<K extends keyof TemplateVariableValue>(field: K, value: TemplateVariableValue[K]) {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }

  function handleNameChange(name: string) {
    setDraft((prev) => ({
      ...prev,
      name,
      key: keyEdited ? prev.key : slugifyVariableKey(name),
    }));
    setErrors((prev) => ({ ...prev, name: undefined, key: undefined }));
  }

  function addChip() {
    const value = chipDraft.trim();
    if (!value || draft.allowedValues.includes(value)) {
      setChipDraft('');
      return;
    }
    set('allowedValues', [...draft.allowedValues, value]);
    setChipDraft('');
  }

  function removeChip(value: string) {
    const next = draft.allowedValues.filter((v) => v !== value);
    setDraft((prev) => ({
      ...prev,
      allowedValues: next,
      // Clear a default that is no longer allowed.
      defaultValue: prev.defaultValue && !next.includes(prev.defaultValue) ? '' : prev.defaultValue,
    }));
  }

  function handleSave() {
    const parsed = templateVariableSchema.safeParse(draft);
    const nextErrors: Partial<Record<keyof TemplateVariableValue, string>> = {};
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof TemplateVariableValue;
        nextErrors[field] ??= issue.message;
      }
    }
    if (draft.key && takenKeys.includes(draft.key)) {
      nextErrors.key ??= 'Another variable already uses this key';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    onSave(parsed.success ? parsed.data : draft);
  }

  const copyToken = () => {
    void navigator.clipboard?.writeText(`{{${token}}}`).catch(() => {});
    toast.success(`Copied {{${token}}}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-medium tracking-tight">
            {isEditing ? 'Edit variable' : 'Add variable'}
          </DialogTitle>
          <DialogDescription>
            Values are captured when a session is created and passed to the agent at the start of
            the call.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Name → live token */}
          <div className="space-y-2">
            <label htmlFor="variable-name" className={labelClass}>
              Name
            </label>
            <Input
              id="variable-name"
              value={draft.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Favorite color"
              aria-invalid={Boolean(errors.name)}
            />
            {errors.name ? <p className="text-xs text-destructive">{errors.name}</p> : null}
            <div className="flex items-center gap-2">
              <span className={helpClass}>In your agent&rsquo;s prompt:</span>
              <button
                type="button"
                onClick={copyToken}
                className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 font-mono text-xs text-primary transition-colors hover:bg-primary/20"
                title="Copy token"
              >
                {`{{${token}}}`}
                <Copy className="h-3 w-3 opacity-60" />
              </button>
            </div>
            {errors.key ? <p className="text-xs text-destructive">{errors.key}</p> : null}
          </div>

          {/* Required toggle */}
          <div className="flex items-center gap-3">
            <Switch
              id="variable-required"
              checked={draft.required}
              onCheckedChange={(checked) => set('required', checked)}
            />
            <label htmlFor="variable-required" className="text-sm">
              Required
              <span className={cn(helpClass, 'ml-2')}>
                must be filled in when creating a session
              </span>
            </label>
          </div>

          {/* Type */}
          <div className="space-y-2">
            <p className={labelClass}>Type</p>
            <div className="flex flex-wrap gap-2">
              {VARIABLE_TYPES.map((t) => (
                <Button
                  key={t}
                  type="button"
                  size="sm"
                  variant={draft.type === t ? 'default' : 'outline'}
                  onClick={() => set('type', t as VariableType)}
                >
                  {VARIABLE_TYPE_LABELS[t]}
                </Button>
              ))}
            </div>
            <p className={helpClass}>{TYPE_HINTS[draft.type]}</p>
          </div>

          {/* Allowed values — surfaced inline so it's discoverable that a
              variable can be a fixed list (renders as a dropdown at capture). */}
          <div className="space-y-2">
            <p className={labelClass}>Allowed values</p>
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-input p-2">
              {draft.allowedValues.map((value) => (
                <Badge key={value} variant="secondary" className="gap-1">
                  {value}
                  <button
                    type="button"
                    onClick={() => removeChip(value)}
                    aria-label={`Remove ${value}`}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <input
                value={chipDraft}
                onChange={(e) => setChipDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addChip();
                  }
                }}
                onBlur={addChip}
                placeholder={
                  draft.allowedValues.length ? 'Add another…' : 'Type a value and press Enter'
                }
                className="min-w-[10rem] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            {errors.allowedValues ? (
              <p className="text-xs text-destructive">{errors.allowedValues}</p>
            ) : (
              <p className={helpClass}>
                Leave empty for free entry. Add values to turn this into a dropdown at capture.
              </p>
            )}
          </div>

          {/* Default */}
          <div className="space-y-2">
            <label htmlFor="variable-default" className={labelClass}>
              Default <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            {draft.allowedValues.length > 0 ? (
              <Select
                value={draft.defaultValue || 'none'}
                onValueChange={(v) => set('defaultValue', v === 'none' ? '' : v)}
              >
                <SelectTrigger id="variable-default">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {draft.allowedValues.map((value) => (
                    <SelectItem key={value} value={value}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="variable-default"
                type={draft.type === 'DATE' ? 'date' : 'text'}
                value={draft.defaultValue}
                onChange={(e) => set('defaultValue', e.target.value)}
                placeholder="Used when left blank"
                aria-invalid={Boolean(errors.defaultValue)}
              />
            )}
            {errors.defaultValue ? (
              <p className="text-xs text-destructive">{errors.defaultValue}</p>
            ) : null}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label htmlFor="variable-description" className={labelClass}>
              Description <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <Input
              id="variable-description"
              value={draft.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Shown under the field when creating a session"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            {isEditing ? (
              'Save variable'
            ) : (
              <>
                <Plus className="h-4 w-4" /> Add variable
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TYPE_HINTS: Record<VariableType, string> = {
  TEXT: 'Free text. Coordinators type or pick from allowed values.',
  NUMBER: 'A number, entered and passed as-is.',
  CURRENCY: 'An amount the agent can speak naturally.',
  DATE: 'A date picker at capture; passed as YYYY-MM-DD.',
};
